import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShoppingCart, Menu, User, Search, Store, Truck, Shield, LogOut, Package, Heart, Bell } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, mediaUrl } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/context/LangContext";
import { Button } from "@/components/ui/button";
import { Logo } from "@/components/Logo";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";

export const Navbar = () => {
  const { user, logout, openAuth } = useAuth();
  const { count } = useCart();
  const { t, lang, toggle } = useLang();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!user) { setUnread(0); return; }
    const fetchUnread = () => api.get("/notifications/unread-count").then((r) => setUnread(r.data.unread || 0)).catch(() => {});
    fetchUnread();
    const id = setInterval(fetchUnread, 15000);
    const onFocus = () => fetchUnread();
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(id); window.removeEventListener("focus", onFocus); };
  }, [user]);

  const doSearch = (e) => {
    e.preventDefault();
    navigate(`/catalog?search=${encodeURIComponent(q)}`);
    setOpen(false);
  };

  const roleLinks = () => {
    if (!user) return null;
    if (user.role === "seller") return <Link to="/seller" className="hover:text-mango-600 transition-colors" data-testid="nav-seller">{t("nav_dashboard")}</Link>;
    if (user.role === "delivery") return <Link to="/delivery" className="hover:text-mango-600 transition-colors" data-testid="nav-delivery">{t("nav_delivery")}</Link>;
    if (user.role === "admin") return <Link to="/admin" className="hover:text-mango-600 transition-colors" data-testid="nav-admin">{t("nav_admin")}</Link>;
    return null;
  };

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-emerald-800/95 border-b border-emerald-900 shadow-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center gap-4 h-28">
        <Logo light />


        <nav className="hidden lg:flex items-center gap-6 text-sm font-semibold text-white ml-4">
          <Link to="/" className="hover:text-mango-600 transition-colors" data-testid="nav-home">{t("nav_home")}</Link>
          <Link to="/catalog" className="hover:text-mango-600 transition-colors" data-testid="nav-catalog">{t("nav_catalog")}</Link>
          {user && <Link to="/orders" className="hover:text-mango-600 transition-colors" data-testid="nav-orders">{t("nav_orders")}</Link>}
          {roleLinks()}
        </nav>

        <form onSubmit={doSearch} className="hidden md:flex items-center flex-1 max-w-md mx-auto">
          <div className="relative w-full">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <input
              data-testid="search-input"
              value={q} onChange={(e) => setQ(e.target.value)}
              placeholder={t("search_ph")}
              className="w-full rounded-full border border-stone-300 bg-white pl-9 pr-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-800/20 focus:border-emerald-800"
            />
          </div>
        </form>

        <div className="flex items-center gap-2 ml-auto md:ml-0">
          <button onClick={toggle} data-testid="lang-toggle" className="text-xs font-semibold px-2 py-1 rounded-md hover:bg-white/15 transition-colors uppercase tracking-wider text-white">
            {lang === "fr" ? "FR" : "EN"} <span className="text-white/40">|</span> <span className="text-white/60">{lang === "fr" ? "EN" : "FR"}</span>
          </button>

          {user && (
            <Link to="/wishlist" className="p-2 rounded-md hover:bg-white/15 transition-colors relative" data-testid="wishlist-link">
              <Heart className="w-5 h-5 text-white" />
              {(user.wishlist?.length || 0) > 0 && <span className="absolute -top-0.5 -right-0.5 bg-emerald-800 text-white text-[10px] font-bold rounded-full w-5 h-5 grid place-items-center">{user.wishlist.length}</span>}
            </Link>
          )}

          <Link to="/cart" className="relative p-2 rounded-md hover:bg-white/15 transition-colors" data-testid="cart-button">            <ShoppingCart className="w-5 h-5 text-white" />
            {count > 0 && <span data-testid="cart-count" className="absolute -top-0.5 -right-0.5 bg-mango-600 text-white text-[10px] font-bold rounded-full w-5 h-5 grid place-items-center">{count}</span>}
          </Link>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="relative flex items-center gap-2 p-1 pr-2 rounded-full hover:bg-white/15 transition-colors" data-testid="user-menu">
                  <span className="w-8 h-8 rounded-full bg-mango-600 text-white grid place-items-center text-sm font-semibold overflow-hidden">{user.avatar ? <img src={mediaUrl(user.avatar)} alt={user.name} className="w-full h-full object-cover" /> : user.name?.[0]?.toUpperCase()}</span>
                  {unread > 0 && <span data-testid="notif-badge" className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full w-5 h-5 grid place-items-center ring-2 ring-emerald-800">{unread > 9 ? "9+" : unread}</span>}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <div className="px-2 py-1.5 text-sm font-medium truncate">{user.name}</div>
                <div className="px-2 pb-1 text-xs text-stone-500 capitalize">{user.role}</div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-notifications"><Bell className="w-4 h-4 mr-2" />Notifications{unread > 0 && <span className="ml-auto bg-red-500 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5">{unread}</span>}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/profile")} data-testid="menu-profile"><User className="w-4 h-4 mr-2" />{t("profile")}</DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/orders")}><Package className="w-4 h-4 mr-2" />{t("nav_orders")}</DropdownMenuItem>
                {user.role === "seller" && <DropdownMenuItem onClick={() => navigate("/seller")}><Store className="w-4 h-4 mr-2" />{t("nav_dashboard")}</DropdownMenuItem>}
                {user.role === "delivery" && <DropdownMenuItem onClick={() => navigate("/delivery")}><Truck className="w-4 h-4 mr-2" />{t("nav_delivery")}</DropdownMenuItem>}
                {user.role === "admin" && <DropdownMenuItem onClick={() => navigate("/admin")}><Shield className="w-4 h-4 mr-2" />{t("nav_admin")}</DropdownMenuItem>}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} data-testid="logout-button" className="text-red-600"><LogOut className="w-4 h-4 mr-2" />{t("logout")}</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <div className="hidden sm:flex items-center gap-2">
              <Button variant="ghost" size="sm" className="text-white hover:bg-white/15 hover:text-white" onClick={() => openAuth("login")} data-testid="login-button">{t("login")}</Button>
              <Button size="sm" className="rounded-full bg-mango-600 hover:bg-mango-700" onClick={() => openAuth("register")} data-testid="register-button">{t("register")}</Button>
            </div>
          )}

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button className="lg:hidden p-2 rounded-md hover:bg-white/15 text-white" data-testid="mobile-menu-toggle"><Menu className="w-5 h-5" /></button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72">
              <form onSubmit={doSearch} className="mt-6 mb-4">
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search_ph")} className="w-full rounded-full border border-stone-300 px-4 py-2 text-sm" data-testid="mobile-search-input" />
              </form>
              <nav className="flex flex-col gap-1 text-base font-medium">
                <Link to="/" onClick={() => setOpen(false)} className="py-2">{t("nav_home")}</Link>
                <Link to="/catalog" onClick={() => setOpen(false)} className="py-2">{t("nav_catalog")}</Link>
                {user && <Link to="/orders" onClick={() => setOpen(false)} className="py-2">{t("nav_orders")}</Link>}
                {user?.role === "seller" && <Link to="/seller" onClick={() => setOpen(false)} className="py-2">{t("nav_dashboard")}</Link>}
                {user?.role === "delivery" && <Link to="/delivery" onClick={() => setOpen(false)} className="py-2">{t("nav_delivery")}</Link>}
                {user?.role === "admin" && <Link to="/admin" onClick={() => setOpen(false)} className="py-2">{t("nav_admin")}</Link>}
                {!user && (
                  <div className="flex flex-col gap-2 mt-4">
                    <Button variant="outline" onClick={() => { openAuth("login"); setOpen(false); }}>{t("login")}</Button>
                    <Button className="bg-emerald-800" onClick={() => { openAuth("register"); setOpen(false); }}>{t("register")}</Button>
                  </div>
                )}
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};
